import React, { useContext, useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { LanguageSelector } from '@unite-us/app-components';
import { find } from 'lodash';
import {get, first, map} from 'lodash';
import { useParams, useNavigate } from 'react-router-dom';
import { buildResponses } from '@unite-us/ui-surveyjs2';
import '@unite-us/ui/dist/main.css';
import '@unite-us/client-utils/dist/main.css';
import TrackerContext from '../utils/context';
import eventConstants from '../utils/eventConstants';
import callOrLog from '../utils/callOrLog';
import './Screenings.css';
import SurveyJSForm from '../utils/index';
import PersonalForm from './components/PersonalForm';
import Consent from '../components/Signature/Consent';
import ScreenConsent from './components/ScreenConsent.js';
import NetworkConsent from './components/NetworkConsent.js';
import useScreenings from './useScreenings.js';
import { createClientScreening } from '../actions';
import formUtils from '../utils/formUtils';
import LANGUAGES from '../utils/languageEnums';

function Screenings() {
  const { coreToken, providerId, templateId , languageCode } = useParams();

  const currentLanguage = sessionStorage.getItem("language");
  const unsupportedLanguage = sessionStorage.getItem("unsupportedLanguage");
  const [state, setState] = useState({
    signature_image: null,
    failedSubmission: false,
  });

  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => setHasErrors(surveyRef.current?.hasErrors()), [hasErrors]);

  useEffect(() => {
    if (languageCode !== undefined && languageCode !== currentLanguage) {
      handleLocaleChange(languageCode);
    }
  }, [languageCode]);

  const { signature_image } = state;
  const data = useScreenings(coreToken, providerId, templateId, currentLanguage);
  const navigate = useNavigate();
  const surveyRef = useRef();
  const eventTracker = useContext(TrackerContext);

  const languages = get(data, 'state.languages', []);

  const setBase64SignatureImage = (newSignature) => {
    setState({ ...state, signature_image: newSignature });
  };

  const goToFirstError = () => {
    setHasErrors(surveyRef.current?.hasErrors())
    const errs = [...document.querySelectorAll('.ui-form-field__error')]
      .filter((a) => a.textContent.includes('Required'));
    if (errs && errs.length > 0) {
      const err = first(errs);
      err.parentElement.parentNode.scrollIntoView();
    }
  };

  const handleSetSurvey = (survey) => {
    surveyRef.current = survey;
  };

  const onSubmit = async (response) => {
    const customForm = get(data, 'state.templateData', null);

    const { Rollbar } = window;
    const coreToken = get(data, 'state.token', null);
    const ref = surveyRef;
    const customFormResponses = ref && ref.current ? buildResponses(ref.current) : [];
    const contactFullName = `${get(response, 'first_name')} ${get(response, 'last_name')}`;

    const pdfFile = await formUtils.generatePDFDynamic({
      base64SignatureImage: signature_image,
      fullName: 'name',
      htmlContent: data.state.consentAgreement.attributes.content,
      contactFullName,
    });

    const requestData = {
      screening: {
        description: response.description,
        ...(
          customForm ? {
            form_id: customForm.id,
            form_response: map(customFormResponses, (r) => ({
              name: r.name,
              title: r.title,
              value: r.value,
              valueId: r.valueId,
              displayValue: r.displayValue,
              id: r.id
            })),
          } : null
        ),
        requestor: response,
        service_type_id: response.service_types,
      },
      template: customForm,
      language: currentLanguage,
    };

    if (!surveyRef.current?.hasErrors()) {
      return createClientScreening(coreToken, pdfFile, providerId, customForm.question_codes, requestData).then((r) => {
        navigate(`/screening/${coreToken}/success`, {
          state: {
            language: currentLanguage,
            data: requestData,
            signature: signature_image,
            languages: languages
          },
        });

        setState({ failedSubmission: false });
      });
    }
  };

  let templateData = get(data, 'state.templateData', null);

  const customForm = {
    "configuration" : templateData ? templateData.survey_json : null
  }
  if (data.isLoading) {
    return <p>Loading..</p>;
  }

  if (data.error) {
    return <p>Error..</p>;
  }

  const unsupportedLanguageFlag = (lc) => {
    const supportedLanguages = LANGUAGES.supportedList;
    const searchedLanguage = find(supportedLanguages, { "value": lc });
    if (searchedLanguage !== undefined) {
      sessionStorage.setItem("unsupportedLanguage", "false");
      return false;
    } else {
      sessionStorage.setItem("unsupportedLanguage", "true");
      return true;
    }
  }

  const handleLocaleChange = (val) => {
    const isUnsupportedLanguage = unsupportedLanguageFlag(val)
    const lc = isUnsupportedLanguage ? "en" : val;
    sessionStorage.setItem("language", lc);
    if (languageCode !== undefined) { // language code on a legacy link is not supported
      navigate(`/assistance-request/${data.state.legacyToken}/${coreToken}/${providerId}/${lc}`);
    }
    window.location.reload();
  }

  return (
    <div className="m-10">
      <Form
        onSubmit={onSubmit}
        initialValues={{}}
        render={({
          handleSubmit, form, submitting, invalid,
        }) => (
          <form onSubmit={handleSubmit}>
            {unsupportedLanguage === "true" ? (
              <div className="mt-2 mb-4 font-bold text-red-700">
                It appears you have followed a direct link for a language that we do not currently support.
                The page here is displayed in English. Please use the language selector to translate it to the supported language of your choice.
              </div>
                ) : null}
            <div className="mb-5">
              <LanguageSelector
                onChange={handleLocaleChange}
                field={{ value: currentLanguage }}
                options={LANGUAGES.supportedList}
              />
            </div>
            <PersonalForm form={form} languagesOptions={languages} />
            <hr className='mt-2 mb-5'></hr>
            {customForm && (
              <>
                {/* <h3 className='mb-2 supplemental-questions ui-h3-heavy'>
                  <FormattedMessage id="5vpskb" description="supplementalQuestions" defaultMessage="Supplemental Questions"/>
                </h3> */}
                <SurveyJSForm
                  id="custom-form"
                  form={customForm}
                  formRef={handleSetSurvey}
                  expanded
                />
              </>
            )}
            <hr className='mt-2 mb-5'></hr>
            <ScreenConsent />
            <hr className='mt-2 mb-5'></hr>
            <NetworkConsent />
            <div className="form-group">
              <Consent handleSignatureEnd={setBase64SignatureImage} failedSubmission={state.failedSubmission} language={currentLanguage} />
              {!signature_image ? (
                <div
                  className="mt-2 mb-5 text-red-700"
                  style={{ marginTop: '-40px' }}
                >
                  <FormattedMessage id="htKh5B" description="signaturePrompt" defaultMessage="Signature Required"/>
                </div>
              ) : ''}
            </div>
            <div className="flex">
              <div>
                {!submitting ? (
                  <button
                    className={`px-8 py-2 mr-8 mb-4 font-bold text-white ${invalid || !signature_image ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900'}`}
                    type="submit"
                    onClick={() => goToFirstError()}
                    disabled={invalid || !signature_image}
                  >
                    <FormattedMessage id="AmnqMW" description="submit" defaultMessage="SUBMIT"/>
                  </button>
                  ) : <FormattedMessage id="KFYDxP" description="submitting" defaultMessage="Submitting...">
                        {txt => <p>{txt}</p>}
                      </FormattedMessage> }
              </div>
              <div>
                {state.failedSubmission ? (
                  <div
                    className="mt-2 font-bold text-red-700"
                  >
                    <FormattedMessage id="9QH2Up" description="failedSubmission" defaultMessage="The form failed to submit. Please submit again."/>
                  </div>
                ) : null}
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default Screenings;
